import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Dog, Eye } from "lucide-react";

import type { EchoflicksSessionEvent, EchoflicksSessionMedia } from "../types";
import { CharacterSheetFigure } from "./CharacterSheetFigure";
import { characterSheetsByGroup, type CharacterSheet } from "./characterSheets";
import {
  dogNameSourceLabel,
  dogPhotoCountLabel,
  dogTitleLabel,
  type DogCastMember,
} from "./dogCast";
import { useDogCast } from "./useDogCast";
import { useMediaUrl } from "./useMediaUrl";

/**
 * "Dog cast" — how one GPT vision call grouped the photos by dog, before it is
 * allowed to change anything.
 *
 * The dog lane is permanently `observe`: it writes a review document but
 * reaches no prompt, so unlike the person cast there is no "applied" state to
 * distinguish. This panel is the whole point of that mode — you can see the
 * grouping an Echo WOULD have been cast with, and decide whether to trust it,
 * without a single delivered film having depended on it.
 *
 * Renders nothing for Echoes with no cast document. The no-census case is
 * already explained by `DogCensusCard` directly above this panel, so there is
 * no admin fallback card here.
 */
export const DogCastSection = ({
  events,
  media,
  isAdmin = false,
}: {
  events: EchoflicksSessionEvent[];
  media: EchoflicksSessionMedia[];
  isAdmin?: boolean;
}) => {
  const { pointer, state } = useDogCast(events);
  const mediaByFilename = useMemo(() => buildMediaMap(media), [media]);
  const sheetsByGroup = useMemo(() => characterSheetsByGroup(events), [events]);

  if (!pointer) return null;

  const cast = state.status === "loaded" ? state.cast : null;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="flex items-center gap-2">
            <Dog className="size-4" />
            Dog cast
          </CardTitle>
          <Badge variant="secondary" className="gap-1">
            <Eye className="size-3" />
            observed only — did not change this echo
          </Badge>
          <Badge variant="outline">mode: {cast?.mode ?? "observe"}</Badge>
          {cast && cast.regroupCount > 0 ? (
            <Badge variant="outline">re-grouped ×{cast.regroupCount}</Badge>
          ) : null}
        </div>
        <CardDescription>
          One GPT vision call grouped the photos by dog; names come from what
          the user said in the interview.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {state.status === "loading" ? (
          <p className="text-sm text-muted-foreground">Loading the cast…</p>
        ) : null}

        {state.status === "error" ? (
          <p className="text-sm text-muted-foreground">
            The census ran ({pointer.castDogCount} dog
            {pointer.castDogCount === 1 ? "" : "s"}), but its review document
            could not be loaded: {state.message}
          </p>
        ) : null}

        {cast && cast.dogs.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No dogs were found in these photos.
          </p>
        ) : null}

        {cast?.dogs.map((dog, index) => (
          <DogCard
            key={dog.castId || index}
            dog={dog}
            index={index}
            mediaByFilename={mediaByFilename}
            isAdmin={isAdmin}
            sheet={sheetsByGroup.get(dog.castId)}
          />
        ))}

        <p className="text-xs text-muted-foreground">
          Visual resemblance only — not pedigree evidence.
        </p>
      </CardContent>
    </Card>
  );
};

const DogCard = ({
  dog,
  index,
  mediaByFilename,
  isAdmin,
  sheet,
}: {
  dog: DogCastMember;
  index: number;
  mediaByFilename: Map<string, EchoflicksSessionMedia>;
  isAdmin: boolean;
  sheet?: CharacterSheet;
}) => {
  // Names only appear when the user gave them. An unnamed group is "Dog 2",
  // never a guess — the lane must not put a name on a dog the user never
  // named, and neither must this panel.
  const title = dogTitleLabel(dog, index);
  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{title}</span>
        <Badge variant="outline">{dogNameSourceLabel(dog)}</Badge>
        <Badge variant="secondary">{dog.breed}</Badge>
        <Badge variant="outline">{dog.breedConfidence} confidence</Badge>
        <span className="text-sm text-muted-foreground">
          {dogPhotoCountLabel(dog.memberFilenames.length)}
        </span>
      </div>
      {dog.descriptor ? (
        <p className="text-sm text-muted-foreground">“{dog.descriptor}”</p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {dog.memberFilenames.map((filename) => (
          <DogThumb
            key={filename}
            filename={filename}
            title={filename}
            media={mediaByFilename.get(filename)}
          />
        ))}
      </div>
      <CharacterSheetFigure sheet={sheet} />
      {isAdmin ? (
        <p className="font-mono text-xs text-muted-foreground">{dog.castId}</p>
      ) : null}
    </div>
  );
};

/**
 * One member photo. Same url/ratio handling as the person cast's FaceThumb,
 * minus the face-box overlay — the dog cast carries no boxes.
 */
const DogThumb = ({
  filename,
  title,
  media,
}: {
  filename: string;
  title: string;
  media?: EchoflicksSessionMedia;
}) => {
  const url = useMediaUrl(
    media
      ? {
          url: media.url,
          storage_bucket: media.storage_bucket,
          storage_path: media.storage_path,
          storage_is_public: media.storage_is_public ?? false,
        }
      : null,
  );
  const [ratio, setRatio] = useState<number | null>(null);

  return (
    <figure className="w-28 space-y-1" title={title}>
      <div
        className="relative overflow-hidden rounded border bg-muted"
        style={ratio ? { aspectRatio: String(ratio) } : { aspectRatio: "1" }}
      >
        {url.url ? (
          <img
            src={url.url}
            alt={filename}
            className="size-full object-contain"
            onLoad={(event) => {
              const image = event.currentTarget;
              if (image.naturalWidth > 0 && image.naturalHeight > 0) {
                setRatio(image.naturalWidth / image.naturalHeight);
              }
            }}
          />
        ) : (
          <div className="flex size-full items-center justify-center text-xs text-muted-foreground">
            {url.status === "error" ? "unavailable" : "…"}
          </div>
        )}
      </div>
      <figcaption className="truncate text-xs text-muted-foreground">
        {filename}
      </figcaption>
    </figure>
  );
};

function buildMediaMap(media: EchoflicksSessionMedia[]) {
  const map = new Map<string, EchoflicksSessionMedia>();
  for (const item of media ?? []) {
    const path = item.storage_path ?? item.url ?? "";
    const filename = String(path).split("/").pop();
    if (filename && !map.has(filename)) map.set(filename, item);
  }
  return map;
}
